import React, { FC, Fragment, useState } from "react";
import { useFrame } from "react-frame-component";
import { useLocation } from "react-router";
import { getAccountCountry, getAccountSlug, getAccountUrl, isLockedPricingSetup } from "src/store/selectors/account";
import { getConditionalRebatesType } from "src/store/selectors/conditionalRebates";
import { getAvailableDealExperiments, getEnabledDealExperiments } from "src/store/selectors/dealExperiments";
import { getIsReserveVehicleEnabled } from "src/store/selectors/electricSettings";
import { switcher } from "src/utils";
import { logError } from "src/utils/logger";
import useSelector from "src/utils/useSelector";

declare global {
    interface Window {
        osAnalytics: any;
    }
}

const ProductAnalytics: FC = ({ children }) => {
    const iframe = useFrame();
    const [initialized, setInitialized] = useState(false);

    const location = useLocation();

    const isProd = useSelector((state) => state.onlineShopper.env === "prod");
    const gaClientId = useSelector((state) => state.tracking.clientId);
    const isAppOpen = useSelector((state) => state.onlineShopper.isAppOpen);
    const accountUrl = useSelector(getAccountUrl);
    const accountSlug = useSelector(getAccountSlug);
    const accountCountry = useSelector(getAccountCountry);
    const lockedPricingEnabled = useSelector(isLockedPricingSetup);
    const reserveVehicleEnabled = useSelector(getIsReserveVehicleEnabled);
    const newVehicleShopper = useSelector((state) =>
        Object.values(state.vehicles.entities).some((vehicle) => vehicle.type === "New")
    );
    const usedVehicleShopper = useSelector((state) =>
        Object.values(state.vehicles.entities).some((vehicle) => vehicle.type === "Used")
    );
    const certifiedVehicleShopper = useSelector((state) =>
        Object.values(state.vehicles.entities).some((vehicle) => vehicle.type === "Certified Used")
    );
    const conditionalRebatesType = useSelector(getConditionalRebatesType);
    const vehicleCount = useSelector((state) => state.vehicles.order.length);
    const isAuthed = useSelector((state) => state.onlineShopper.isAuthed);
    const dealUUID = useSelector((state) => state.deal.uuid);
    const pageType = useSelector((state) => state.onlineShopper.pageType);
    const enabledDealExperiments = useSelector(getEnabledDealExperiments);
    const availableDealExperiments = useSelector(getAvailableDealExperiments);

    // INITIALIZE TRACKER
    React.useEffect(() => {
        try {
            if (!initialized && gaClientId?.length && isAppOpen) {
                iframe.window.osAnalytics("config", "G-ZQMCC9TNWY", {
                    send_page_view: false,
                    client_id: gaClientId,
                    page_type: pageType,
                    ...(!isProd && { traffic_type: "internal" }),
                });
                setInitialized(true);
            }
        } catch (e) {
            logError(e);
        }
    }, [initialized, gaClientId, isAppOpen, isProd, pageType, iframe.window]);

    // TRACK USER PROPERTIES
    React.useEffect(() => {
        try {
            if (initialized && isAppOpen) {
                iframe.window.osAnalytics("set", "user_properties", {
                    locked_pricing_enabled: lockedPricingEnabled,
                    account_slug: accountSlug,
                    account_country: accountCountry,
                    reserve_vehicle_enabled: reserveVehicleEnabled,
                    vehicle_types: `New:${newVehicleShopper}|Used:${usedVehicleShopper}|CPO:${certifiedVehicleShopper}`,
                    conditional_rebates: conditionalRebatesType,
                    vehicle_count: vehicleCount,
                    ...(isAuthed && { user_id: dealUUID }),
                    ...(!isProd && { traffic_type: "internal" }),
                    ...(!!availableDealExperiments && {
                        experiments: availableDealExperiments,
                    }),
                    ...(!!enabledDealExperiments && {
                        enabledExperiments: enabledDealExperiments,
                    }),
                });
            }
        } catch (e) {
            logError(e);
        }
    }, [
        initialized,
        isAppOpen,
        accountSlug,
        accountCountry,
        lockedPricingEnabled,
        reserveVehicleEnabled,
        newVehicleShopper,
        usedVehicleShopper,
        certifiedVehicleShopper,
        conditionalRebatesType,
        vehicleCount,
        isAuthed,
        dealUUID,
        isProd,
        enabledDealExperiments,
        availableDealExperiments,
        iframe.window,
    ]);

    // TRACK PAGE VIEWS
    React.useEffect(() => {
        try {
            if (initialized && isAppOpen) {
                if (location.pathname === "/") {
                    return;
                }

                const pageParameters = {
                    ...(!isProd && { traffic_type: "internal" }),
                    page_location: accountUrl,
                    source: accountUrl,
                    page_referrer: accountUrl,
                    page_path: location.pathname,
                    page_title: switcher({
                        "/payment-builder": "Payment Builder",
                        "/my-garage": "Garage",
                        "/my-garage/trade-in": "Garage - Trade In Drawer",
                        "/my-garage/payment-settings": "Garage - Payment Settings Drawer",
                        "/my-garage/conditional-rebates": "Garage - Conditional Rebates Drawer",
                        "/my-garage/accessories": "Garage - Accessories Drawer",
                        "/create": "Lead Gate",
                        "/unlock-garage": "Pin Code",
                        "/access-my-garage": "Sign In",
                        "/continue-deal": "Continue Deal",
                        "/continue-deal/customize-vehicle": "Continue Deal - Customize Vehicle",
                        "/continue-deal/schedule-appointment": "Continue Deal - Schedule Appointment",
                        "/continue-deal/credit-application": "Continue Deal - Credit Application",
                        "/continue-deal/credit-application/creditiq": "Continue Deal - Credit IQ Checkout",
                        "/dealer-offer": "Dealer Offer",
                        "/dealer-offer/review": "Dealer Offer - Review",
                    })(location.pathname)(location.pathname),
                };
                iframe.window.osAnalytics("event", "page_view", pageParameters);
                iframe.window.osAnalytics("set", pageParameters);
            }
        } catch (e) {
            logError(e);
        }
    }, [initialized, location, isAppOpen, accountUrl, isProd, iframe.window]);

    return <Fragment>{children}</Fragment>;
};

export default ProductAnalytics;
